// The kadmon panel — T35's find drawer with a memory, T55 (9 Sep 2026), agents doc § 7.2.
//
// A RESULTS CHAT, NOT A CHATBOT. The recruiter types a need; kadmon answers with candidate cards —
// name, current role and employer, the stage on the chat's search — and a reason each that quotes
// what is on the record, what they told kadmon, or a note a recruiter wrote. Follow-ups refine. It
// answers about people in our database and nothing else, and since T116 (26 Sep 2026) it ranks
// and scores when a recruiter asks: the read is the model's, the decision is the recruiter's.
//
// WHAT T55 CHANGED. The drawer used to forget the conversation the moment it closed. Now every
// question belongs to a CHAT, chats are on the left, and the conversation is on the right —
// **one shared history**: every recruiter sees every chat, sees who started it, and may continue
// any of them (Natanel, 8 Sep 2026: "one history"). Nothing about the conversation is held in
// this app: the turns come from the service on every open and every poll (api/chats.ts), which is
// what lets Lorenz carry on where Mona left off in a tab that never saw her question.
//
// TWO DOORS, ONE PANEL, AND A NEW CHAT AT EACH. The ⌘K palette opens it with a sentence
// (`?find=<sentence>`), and the search page's button opens it pre-filled from the role brief
// (`?find=brief`). Both start a new chat with that Search as context; `?chat=<id>` continues one.
// The panel lives in the URL so it survives a reload and closes by dropping the parameters; it is
// mounted once, in the shell, because the palette that opens it closes as it does.
//
// A CHAT IS WRITTEN BY ASKING, never by opening. **New chat** clears the right-hand side and
// nothing else — no row exists until a question is asked in it — so an abandoned chat leaves no
// trace in a shared list everybody reads.
//
// THE ONLY WRITE IS THE RECRUITER'S CLICK. "Add to search" on a card is the pipeline PUT the
// search page already makes (api/searches.ts `useSetStage`, at `sourced`); "Add all" is the same
// press for every card. Nothing is added on the model's say-so, and the service logs every
// question — the note at the foot says both, because they are what makes this surface safe to
// trust. Since T56 the same rule covers every other write kadmon proposes: a CONFIRM CARD, tapped
// or, since T66a, said yes to (twice for a removal).
//
// THE CONFIRM CARD (T56, 10 Sep 2026, agents doc § 7.3). Ask kadmon to do something the brain can
// do — enter a candidate, put them on a Search, note, tag, rate, put forward, invite, pause,
// message, clear an opt-out — and her answer comes with a card under it: every step in the
// brain's own words, **Confirm** and **Dismiss**. Nothing happened when the card appeared; the
// service stored the proposal and this app draws it. Confirm asks the service to apply THAT
// stored card as the recruiter who tapped; the outcome — done, stopped at a step with the form's
// own reason, waiting on the worker for a new person's record — is drawn on the same card, in the
// history, so a chat is also the record of what kadmon did. The card is tapped (T57) — or, in voice mode, said yes to (T66a).
//
// TAP TO TALK (T57, 10 Sep 2026, agents doc § 7.4). A mic between the composer and Ask, where the
// build and the browser have an engine (app/voice.tsx — none on the deployed brain until T57b
// brings Amazon Transcribe). What it hears fills the composer and waits there: Ask is the only way a
// question reaches kadmon, so a misheard name is put right before it is in a chat everybody reads.
// The answer to a dictated question is read aloud as well as drawn; a card is tapped (T595).
//
// EVERY ASK STREAMS (T81, 20 Sep 2026, agents doc § 7.6 item 4). One ask, one route:
// `POST /find/stream`, which voice mode has used since T66b. The answer is drawn a sentence at a
// time as the model writes it, instead of a spinner for the twenty seconds a question that reads
// a screen or two takes (T73's six live questions: 2.9 s for one round, 20.7 s for several). The
// request is the same one, so this costs no extra call and nothing is held open between them —
// the brain tier stays polling-only (CLAUDE.md § Brain tier). What the events build is
// `src/writing.ts`; a dictated answer is still spoken whole at the end, as T57 made it.
// A FILE ON A QUESTION (T83, 22 Sep 2026, agents doc § 7.6 item 6). The paper-clip beside the
// composer attaches a list — a .csv or an .xlsx — to the next question, and kadmon reads it with the
// import's own preview: which importer it is, the tab, the country and the client and role for
// rows naming none are hers to work out from what the recruiter said, and everything about what a
// ROW means stays the import's. The preview comes back on the answer and is drawn under it by
// app/ImportCard.tsx, whose Import runs the import's own calls from this tab on the ROWS that
// preview gave. The file itself goes up once, with the question, and is stored by nobody.
// ANY FILE SINCE T154a (28 Sep 2026): a PDF, a Word document, a photo, a text file — read in the
// question's request by the recruiters' reading, as a file sent on WhatsApp is read; what she read
// is kept on the question and shown under it (`FilesUnder`), the file by nobody. A file may go
// with no words at all: she says what it is and asks what to do with it (T154d).
import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useClerk } from '@clerk/react';
import { Link, useLocation, useMatch, useSearchParams } from 'react-router-dom';
import type {
  ActionCard,
  ActionStatus,
  ChatFile,
  ChatSummary,
  ChatTurn,
  FindCard,
  FindResponse,
  ImportProposal,
  ReasonSource,
} from '@vera/bff';
import { joinPicked } from '@vera/db/picked-options';
import { ApiError } from '../api/core.ts';
import {
  useConfirmAction,
  useConfirmCardWithFile,
  useConfirmGroup,
  useDismissAction,
  useDismissGroup,
  useSettleAction,
} from '../api/actions.ts';
import { useChat, useChats } from '../api/chats.ts';
import {
  ATTACH_ACCEPT,
  attachMaxBytes,
  attachmentBase64,
  fileSize,
  readAttachment,
  type AskAttachment,
} from '../importChat.ts';
import { ChatDownloads } from './ChatDownloads.tsx';
import { answerSignsOut, onSignOutHere, SIGN_OUT_AFTER_MS } from './sign-out.ts';
import { ImportCard } from './ImportCard.tsx';
import { useFindStreaming, useFollowFind, type FindProgressHandlers } from '../api/find.ts';
import { useVoiceLookup } from '../api/voice.ts';
import { POLL_MS } from '../api/intervals.ts';
import { useMe } from '../api/me.ts';
import { KEYS } from '../api/mutation.ts';
import { useSearch, useSetStage } from '../api/searches.ts';
import { cardUnits, doneLinesOf, groupNoun, tallyOf, type CardUnit } from '../card-groups.ts';
import { optionsUnder, yesNoUnder } from '../answer-yes-no.ts';
import {
  failureStays,
  mayTakeUrl,
  nextAsk,
  replyBody,
  shouldFollow,
  fileBack,
  wordsBack,
  type NextAsk,
  type Outcome,
  type Queued,
  type ReplyTo,
} from '../follow-ups.ts';
import { formatRelative } from '../format.ts';
import { withParams } from '../lists.ts';
import { STAGE_LABELS } from '../pipeline.ts';
import { Avatar, Badge, Button, Icon, VeraOrb, type Tone, FirmName } from '../ui/atoms.tsx';
// T176 (30 Sep 2026): her answer is light markdown now, a bold name, a ranked list, a LinkedIn.
import { Markdown } from '../ui/Markdown.tsx';
import { turnsToDraw, UNWRITTEN, waitingLine, writingHandlers, type Writing } from '../writing.ts';
import { useScrollLock } from './scrollLock.ts';
import {
  MicButton,
  TalkButton,
  useQuietWhenClosed,
  useTapToTalk,
  useVoiceMode,
  useVoiceStage,
  VoiceStage,
} from './voice.tsx';
import '../styles/screens/find.css';

/** The URL parameter that opens the panel. Any value opens it; two values mean something. */
export const FIND_PARAM = 'find';
/** The chat on screen. Absent means a new one, which no row exists for until it is asked of. */
export const CHAT_PARAM = 'chat';
/** Pre-fill the question from the role brief of the search on screen. */
export const FIND_FROM_BRIEF = 'brief';
/** Open empty. */
export const FIND_EMPTY = '1';

/** What the palette passes when Enter was pressed on a sentence: ask it at once. */
export interface FindNavigationState {
  ask?: boolean;
}

/**
 * How a reason's source reads on a card. The section names are the roster's (`@vera/agent/find`);
 * since 24 Sep 2026 a quote may also come from what she read about the person for the question.
 */
const SOURCE_LABEL: Record<ReasonSource, string> = {
  record: 'record',
  told_vera: 'told kadmon',
  answers: 'questions',
  note: 'your note',
  search: 'pipeline',
  tag: 'tag',
  verification: 'verification',
  thread: 'thread',
  meeting: 'meeting',
  read: 'looked up',
};

const NOT_SET_UP =
  'Asking kadmon is not set up on this server: Claude is not configured here. ' +
  'Nothing here can ask kadmon until it is.';

/**
 * T83: the preview of an attached file, and the file it was made from — held ABOVE the keyed panel
 * (below), because a new chat's first answer writes its id into the URL and that REMOUNTS the
 * panel. Held inside it, the card drawn under that very answer would vanish as it appeared: found
 * on the render harness, where the ask went through and the card never drew.
 */
interface PanelImports {
  queryId: string;
  proposals: ImportProposal[];
  /**
   * T111: the file the question carried, kept only while its card is — a LinkedIn network's
   * Import sends it again, because the service plans the whole network at the tap.
   */
  file: AskAttachment | null;
}

/**
 * T360 E3 3b: the files attached to this window's questions, by the question's id, in base64 — for
 * a Drive card that saves one, whose Confirm sends it again (the brain keeps no file). Held above
 * the keyed panel like the previews, so a remount keeps them; a reload loses them, and the card
 * then says to attach the file again. The last three only.
 */
const AskedFiles = createContext<Readonly<Record<string, string>>>({});
const ASKED_FILES_KEPT = 3;

export function VeraPanel() {
  const [params] = useSearchParams();
  const match = useMatch('/searches/:searchId');
  const opener = params.get(FIND_PARAM);
  // Out here, above the keyed panel: a closed panel stops kadmon reading aloud, however it closed.
  useQuietWhenClosed(opener !== null);
  // T83: and the attached file's preview, for the same reason a remount must not lose it.
  const [imports, setImports] = useState<PanelImports | null>(null);
  const [askedFiles, setAskedFiles] = useState<Readonly<Record<string, string>>>({});
  if (opener === null) return null;
  const chatId = params.get(CHAT_PARAM);
  const searchId = match?.params.searchId ?? null;
  // Keyed on the chat: switching chats — or starting a new one on another mandate — clears the
  // draft and the pending question with it, rather than carrying them into somebody else's thread.
  return (
    <AskedFiles.Provider value={askedFiles}>
      <Panel
        key={chatId ?? `new:${searchId ?? 'palette'}`}
        chatId={chatId}
        searchId={searchId}
        opener={opener}
        imports={imports}
        onImports={setImports}
        onAskedFile={(queryId, base64) =>
          setAskedFiles((held) =>
            Object.fromEntries(
              [...Object.entries(held), [queryId, base64] as const].slice(-ASKED_FILES_KEPT),
            ),
          )
        }
      />
    </AskedFiles.Provider>
  );
}

function Panel({
  chatId: chatInUrl,
  searchId,
  opener,
  imports,
  onImports,
  onAskedFile,
}: {
  chatId: string | null;
  searchId: string | null;
  opener: string;
  /** T83: the attached file's preview, held above this component so a remount cannot lose it. */
  imports: PanelImports | null;
  onImports: (imports: PanelImports) => void;
  /** T360 E3 3b: the file this question carried, for a Drive card that saves it. */
  onAskedFile: (queryId: string, base64: string) => void;
}) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const me = useMe();
  // A new chat's id, held here while voice mode is on (17 Sep 2026). The panel is keyed on the
  // URL's chat, so writing a new chat's id there remounts it — in voice mode mid-answer, when the
  // orb restarted and jumped to the top rather than moving there. So a conversation's new chat is
  // read by the id held here, and the URL takes it once the conversation has ended.
  const [held, setHeld] = useState<string | null>(null);
  const chatId = chatInUrl ?? held;
  const chats = useChats(true);
  const chat = useChat(chatId);
  const findStreaming = useFindStreaming();
  const voiceLookup = useVoiceLookup();
  const setStage = useSetStage();
  const body = useRef<HTMLDivElement>(null);
  const composer = useRef<HTMLTextAreaElement>(null);

  const [draft, setDraft] = useState(
    opener === FIND_FROM_BRIEF || opener === FIND_EMPTY ? '' : opener,
  );
  /** The question in flight, drawn before the service has answered it. */
  const [pending, setPending] = useState<string | null>(null);
  /** T570 (review round 2): the thread's turns when it was asked (`turnsToDraw`'s `before`). */
  const [askedOver, setAskedOver] = useState<ReadonlySet<string>>(() => new Set());
  /** T81: her answer to it as far as she has written it, under that question. */
  const [writing, setWriting] = useState<Writing>(UNWRITTEN);
  const [failure, setFailure] = useState<unknown>(null);
  /** T83: the list attached to the next question, and the previews the last one came back with. */
  const [attached, setAttached] = useState<File | null>(null);
  const [tooBig, setTooBig] = useState(false);
  /** T154a: the file the question in flight carries, named under it while she reads. */
  const [pendingFile, setPendingFile] = useState<string | null>(null);
  // T561 (31 Oct 2026): a Reply on a message of the chat, whole or its highlighted words: held on
  // the composer until the next question takes it, and drawn above the question in flight.
  const [replying, setReplying] = useState<ReplyTo | null>(null);
  const replyingRef = useRef(replying);
  replyingRef.current = replying;
  const [pendingReply, setPendingReply] = useState<string | null>(null);
  /** The message of the chat holding a highlight, by `replyKey`, and the words highlighted. */
  const [selected, setSelected] = useState<{ key: string; text: string } | null>(null);
  const attachInput = useRef<HTMLInputElement>(null);
  const [added, setAdded] = useState<ReadonlySet<string>>(new Set());
  const [adding, setAdding] = useState(false);
  /** On a phone the two halves are two screens; on a desktop they are two columns. */
  const [listOnPhone, setListOnPhone] = useState(false);
  // Tap to talk (T57): what is heard lands in the composer, and Ask is still the only way in.
  const talk = useTapToTalk({
    draft,
    setDraft,
    // Straight to the composer on a desktop, so Enter asks; not on a phone, where focus would put
    // the keyboard over the words the recruiter is there to check.
    onHeard: () => {
      if (window.matchMedia('(pointer: fine)').matches) composer.current?.focus();
    },
  });

  // The panel exists only while the drawer is open: the page is held for exactly that long, and
  // a swipe in the thread scrolls the thread, not the search page under it (app/scrollLock.ts).
  useScrollLock(true);

  const enabled = me.data ? me.data.findEnabled : true;
  // The chat keeps the Search it was opened from; a chat that was opened from none stays that way
  // even when the panel is open on a search page.
  const context = chatId ? (chat.data?.searchId ?? null) : searchId;
  const turns = chat.data?.turns ?? [];
  const close = () =>
    setParams(withParams(params, { [FIND_PARAM]: undefined, [CHAT_PARAM]: undefined }), {
      replace: true,
    });
  const show = (id: string | null) =>
    setParams(withParams(params, { [FIND_PARAM]: FIND_EMPTY, [CHAT_PARAM]: id ?? undefined }), {
      replace: true,
    });

  // The chat a question goes to (17 Sep 2026): this panel's chat, or the id a new chat's first
  // answer brought, which a spoken follow-up can need before the panel has rendered it — asked
  // without the id, it would open a second chat.
  const chatNow = useRef(chatId);
  // A question is in flight: `pending` holds its words. Since T446d (25 Oct 2026) the composer, the
  // mic and Ask stay open meanwhile, and what is sent waits in a queue (follow-ups.ts) that goes
  // when her answer ends: typed text folds into the question in flight, a file or a dictated
  // question is answered alone, after. Voice mode's asks and the Yes/No taps never queue.
  const inFlight = useRef(false);
  const asking = pending !== null;
  // The queue is read from async callbacks, so it lives in a ref as well as in state.
  const [queue, setQueue] = useState<readonly Queued[]>([]);
  const queueRef = useRef<readonly Queued[]>([]);
  const setQueued = (next: readonly Queued[]) => {
    queueRef.current = next;
    setQueue(next);
  };
  // The composer is read once by `enqueue`, and a second Enter in the same tick must find it empty.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const attachedRef = useRef(attached);
  attachedRef.current = attached;
  const turnsRef = useRef(turns);
  turnsRef.current = turns;
  /** The question in flight: its row, whether a typed follow-up may fold into it, and the stamp. */
  const flight = useRef<{
    words: string;
    replyTo?: ReplyTo;
    queryId: string | null;
    foldable: boolean;
    stamped: boolean;
  } | null>(null);
  /** A follow-up is behind the question in flight: her draft is cleared and she is reading both. */
  const [folding, setFolding] = useState(false);
  const followFind = useFollowFind();
  // T570a: Clerk's own sign-out, for an answer that signed this tab out (sign-out.ts).
  const clerk = useClerk();
  // And for a sign_out card this tab confirms later (R9, sign-out.ts `confirmedHere`).
  useEffect(() => onSignOutHere(() => void clerk.signOut()), [clerk]);

  /** Stamp the question in flight when the queue's head is a typed message that may fold into it. */
  const maybeFollow = () => {
    const f = flight.current;
    if (!f || !f.queryId) return;
    if (!shouldFollow(queueRef.current, f.foldable, f.stamped, f.words)) return;
    f.stamped = true;
    setFolding(true);
    setWriting(UNWRITTEN);
    void followFind(f.queryId);
  };

  /**
   * Ask one thing. Takes the words, the file and the dictated mark as arguments and reads neither
   * the composer nor the mic (T446d: a queued item must not steal what is typed since, nor cut a
   * read-aloud off). `chained` is a queue's next item: the last failure stays on screen.
   */
  const send = async (
    item: NextAsk,
    // T66b: voice mode hears the answer as it is written, on the streamed find. Since T81 the
    // screen is written to from the same events, whether or not anybody is listening.
    options: { viaVoice?: boolean; on?: FindProgressHandlers; chained?: boolean } = {},
  ): Promise<FindResponse | null> => {
    const { viaVoice = false, on, chained = false } = options;
    const content = item.words.trim();
    const file = item.file ?? null;
    // One ask at a time, on a ref rather than on `pending`: two sends in one tick both read the
    // state as it was and would open two questions in the chat. T154d: a file alone is a question.
    if ((!content && !file) || inFlight.current || !enabled) return null;
    inFlight.current = true;
    // A dictated question is answered aloud; a typed one is not (T57). And it reaches kadmon marked
    // as dictated (T57a): the mic hears names badly, so she reads a name that fits nobody on file
    // by sound and puts the person she matched on the card — the mark rides on the ask, nowhere
    // else, and the chat draws the words as they were heard.
    // Voice mode's turns (T66a) are spoken too, and read by the loop itself, not here.
    const spoken = item.spoken === true || viaVoice;
    flight.current = {
      words: content,
      ...(item.replyTo ? { replyTo: item.replyTo } : {}),
      queryId: null,
      foldable: false,
      stamped: false,
    };
    setPending(content);
    setAskedOver(new Set(turnsRef.current.map((turn) => turn.queryId)));
    setPendingFile(file ? file.name : null);
    setPendingReply(item.replyTo?.shown ?? null);
    setWriting(UNWRITTEN);
    setFolding(false);
    if (!chained) setFailure(null);
    let outcome: Outcome = 'failed';
    try {
      // T83: the file rides on this question and no other; `enqueue` has already refused one too
      // big, so a refusal here is only a file that changed under us.
      let attachment = null;
      if (file) {
        const read = await readAttachment(file, attachMaxBytes(file));
        if ('tooBig' in read) return null;
        attachment = read;
      }
      const body = {
        chatId: chatNow.current,
        searchId: context,
        ask: content,
        ...(spoken ? { spoken: true } : {}),
        ...(attachment ? { file: attachment } : {}),
        ...(item.replyTo ? { replyTo: replyBody(item.replyTo) } : {}),
      };
      // A dictated question's answer is read aloud on the stream's answer event (24 Sep 2026), as
      // voice mode hears it — not once the chat was refetched, which is where until then the reading
      // waited (the latency audit: every tap to talk paid for the chat's refetch before her first
      // word). The event is the answer the brain has taken (T202 K-23, 5 Oct 2026), never a draft.
      let readAloud = false;
      const hear: FindProgressHandlers | undefined =
        spoken && !viaVoice
          ? {
              ...on,
              answer: (text, cards) => {
                on?.answer?.(text, cards);
                readAloud = true;
                talk.speak(text);
              },
            }
          : on;
      const writes = writingHandlers(setWriting, {
        ...hear,
        // T446d: the stream's first line names the chat and the row. A new chat's id is held from
        // here (the URL takes it once nothing is in flight or queued: a remount would lose the
        // queue), and a typed message already waiting may stamp the row.
        opened: ({ chatId: opened, queryId, foldable }) => {
          if (opened !== chatNow.current) {
            chatNow.current = opened;
            setHeld(opened);
          }
          if (flight.current) {
            flight.current.queryId = queryId;
            flight.current.foldable = foldable;
          }
          maybeFollow();
        },
      });
      const response = await findStreaming(body, {
        ...writes,
        // Her half-written first answer is gone the moment a follow-up is stamped.
        sentence: (text) => {
          if (!flight.current?.stamped) writes.sentence?.(text);
        },
      });
      if ('superseded' in response) {
        outcome = 'superseded';
        return null;
      }
      outcome = 'answered';
      if (!failureStays(chained, outcome)) setFailure(null);
      // A new chat learns its id here, and the URL carries it from now on — a reload, or a link
      // pasted to the recruiter beside you, opens the same conversation. In voice mode, from the
      // end of the conversation (`held`). Since T446d the id usually arrived on the `opened` line.
      if (response.chatId !== chatNow.current) {
        chatNow.current = response.chatId;
        // The URL takes it once the composer is empty too (the effect below): see `mayTakeUrl`.
        setHeld(response.chatId);
      }
      // T83: the previews this answer made of the attached file, drawn under the turn that asked
      // for them. Held here and in no table: a preview holds a file's rows (ImportCard.tsx).
      if (attachment) onAskedFile(response.queryId, attachmentBase64(attachment));
      if (file && response.imports.length > 0) {
        onImports({ queryId: response.queryId, proposals: response.imports, file: attachment });
      }
      // Read on across the remount that id causes: the reading lives beside the engine. An answer
      // the stream never marked as written is read now, as before.
      if (spoken && !viaVoice && !readAloud) talk.speak(response.answer);
      // T570a: asked to sign out, this tab does, once its answer has shown (sign-out.ts).
      if (answerSignsOut(response.actions)) {
        window.setTimeout(() => void clerk.signOut(), SIGN_OUT_AFTER_MS);
      }
      return response;
    } catch (error) {
      // The service logged the question before it asked the model, so a failed ask is a turn in
      // the chat with no answer under it. Refetch to show it as it stands rather than inventing.
      setFailure(error);
      // T561 review round 1: a refused Reply is refused before the question is logged, so its
      // words go back on the composer, ahead of anything typed there since, and its file with them.
      const back = wordsBack(
        error instanceof ApiError ? error.code : null,
        draftRef.current,
        content,
      );
      if (back !== null) {
        draftRef.current = back;
        setDraft(back);
      }
      const fileAgain = fileBack(
        error instanceof ApiError ? error.code : null,
        attachedRef.current,
        file,
      );
      if (fileAgain) {
        attachedRef.current = fileAgain;
        setAttached(fileAgain);
      }
      if (chatId) void chat.refetch();
      void chats.refetch();
      return null;
    } finally {
      inFlight.current = false;
      flight.current = null;
      setPending(null);
      setPendingFile(null);
      setPendingReply(null);
      setFolding(false);
      // The next of the queue goes at once, in the same batch, so the box never looks idle between.
      const next = nextAsk(queueRef.current, outcome, content, item.replyTo);
      setQueued(next.rest);
      if (next.ask) void sendRef.current(next.ask, { chained: true });
    }
  };
  const sendRef = useRef(send);
  sendRef.current = send;

  /**
   * An ask that never queues: voice mode's turns, a Yes or a No tapped under her answer, and the
   * palette's sentence. The composer and the file on it stay where they are, bar the sentence.
   */
  const ask = async (
    text: string,
    viaVoice = false,
    on?: FindProgressHandlers,
    // T240: a Yes or a No tapped under her answer, asked alone: the words half typed on the
    // composer stay where they are.
    alone = false,
  ): Promise<FindResponse | null> => {
    if (!text.trim() || inFlight.current || !enabled || talk.mode === 'listening') return null;
    const spoken = talk.beginAsk() || viaVoice;
    if (!alone) {
      draftRef.current = '';
      setDraft('');
    }
    return send({ words: text, ...(spoken ? { spoken: true as const } : {}) }, { viaVoice, on });
  };
  const askRef = useRef(ask);
  askRef.current = ask;

  /**
   * The composer's way in (Enter, Ask). Reads it once and clears it. Nothing in flight: asked at
   * once. Otherwise queued, and a typed head may stamp the question in flight (`maybeFollow`).
   */
  const enqueue = () => {
    if (!enabled || talk.mode === 'listening' || voice.state !== 'off') return;
    const words = draftRef.current.trim();
    const file = attachedRef.current;
    if (!words && !file) return;
    // T83: a file too big to send ends the ask, and the words stay on the composer with it.
    if (file && tooBig) return;
    const spoken = talk.beginAsk();
    const replyTo = replyingRef.current ?? undefined;
    draftRef.current = '';
    attachedRef.current = null;
    replyingRef.current = null;
    setDraft('');
    setReplying(null);
    if (file) {
      setAttached(null);
      setTooBig(false);
      if (attachInput.current) attachInput.current.value = '';
    }
    const item: Queued = {
      kind: file ? 'file' : spoken ? 'spoken' : 'text',
      words,
      ...(file ? { file } : {}),
      ...(file && spoken ? { spoken: true as const } : {}),
      ...(replyTo ? { replyTo } : {}),
    };
    if (!inFlight.current && queueRef.current.length === 0) {
      void send({
        words,
        ...(file ? { file } : {}),
        ...(spoken ? { spoken: true as const } : {}),
        ...(replyTo ? { replyTo } : {}),
      });
      return;
    }
    setQueued([...queueRef.current, item]);
    maybeFollow();
  };

  // Voice mode (T66a): the hands-free loop, bound to this panel's ask. The loop lives beside the
  // engines, so it survives the panel's remount when the recruiter opens another chat
  // mid-conversation; its callbacks are rebound on every render, so the ask it calls next is this one's.
  const voice = useVoiceMode({
    ask: async (text, on) => {
      const response = await askRef.current(text, true, on);
      if (!response) return null;
      return {
        answer: response.answer,
        // T595: what is left waiting for a tap, which she says; nothing she hears applies it.
        card: response.actions.find((card) => card.status === 'proposed')?.id ?? null,
        // 24 Sep 2026: whom the answer as streamed named, who have no card — she says so.
        ...(response.unshown ? { unshown: response.unshown } : {}),
        // 17 Sep 2026: people or cards on screen, which the orb moves out of the way of.
        showing: response.matches.length > 0 || response.actions.length > 0,
      };
    },
    // 23 Sep 2026: her voice's own lookups, answered from the records — a person's fact, and since
    // 24 Sep a Search's people and who is waiting. Each goes to this chat, or opens one as an ask
    // does, and the new chat's id is held the same way.
    lookUp: async (input) => {
      const found = await voiceLookup({
        ...input,
        chatId: chatNow.current,
        searchId: context,
      });
      if (found.found === 'answer' && found.chatId !== chatNow.current) {
        chatNow.current = found.chatId;
        setHeld(found.chatId);
      }
      return found;
    },
  });
  // Where the voice orb sits over the chat (voice-stage.ts); at the top, the chat scrolls to what
  // she has just put on screen.
  const stage = useVoiceStage(voice);
  const docked = voice.state !== 'off' && stage.layout === 'docked';
  // T561: which message of the chat holds a highlight, so its Reply quotes just those words. Both
  // ends of the selection inside one message, or none.
  useEffect(() => {
    const onChange = () => {
      const selection = document.getSelection();
      const text = selection?.toString().trim() ?? '';
      const holder = (node: Node | null | undefined) =>
        (node instanceof Element ? node : node?.parentElement)?.closest<HTMLElement>(
          '[data-reply]',
        ) ?? null;
      const at = holder(selection?.anchorNode);
      const key = at?.dataset.reply;
      const inside =
        at !== null && body.current?.contains(at) && holder(selection?.focusNode) === at;
      setSelected(text !== '' && inside && key ? { key, text } : null);
    };
    document.addEventListener('selectionchange', onChange);
    return () => document.removeEventListener('selectionchange', onChange);
  }, []);
  useEffect(() => {
    if (docked) body.current?.scrollTo({ top: body.current.scrollHeight });
  }, [docked]);
  // The conversation over, the URL takes the new chat it opened: the remount costs nothing now.
  useEffect(() => {
    // T446d: and nothing in flight or queued, since the panel is keyed on the URL's chat and a
    // remount would lose the queue.
    if (
      voice.state === 'off' &&
      held !== null &&
      chatInUrl === null &&
      mayTakeUrl({
        asking,
        queued: queue.length,
        draft,
        attached: attached !== null,
        talk: talk.mode,
      })
    )
      show(held);
  }, [voice.state, held, chatInUrl, asking, queue.length, draft, attached, talk.mode]);

  // From the palette, Enter on a sentence is the click: ask it as the panel opens. Once, and
  // only for a sentence — a reload of the URL opens the panel with the sentence in the composer
  // and waits, so a shared link never spends a model call on its own.
  const autoAsk =
    (location.state as FindNavigationState | null)?.ask === true &&
    chatId === null &&
    opener !== FIND_FROM_BRIEF &&
    opener !== FIND_EMPTY &&
    opener.trim() !== ''
      ? opener
      : null;
  const autoAsked = useRef(false);
  useEffect(() => {
    if (autoAsk && !autoAsked.current) {
      autoAsked.current = true;
      void askRef.current(autoAsk);
    }
  }, [autoAsk]);

  // The thread follows what is on screen: a new turn, a question just asked — and since T81 each
  // sentence of the answer as it arrives, so a long one writes itself into view rather than off
  // the bottom of it.
  useEffect(() => {
    body.current?.scrollTo({ top: body.current.scrollHeight });
  }, [turns.length, pending, writing.text]);

  useEffect(() => {
    if (turns.length === 0) composer.current?.focus();
  }, [turns.length]);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const onComposerKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter asks; Shift+Enter is a new line, as in every chat a recruiter has used.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      enqueue();
    }
  };

  const add = async (cards: FindCard[]) => {
    if (!context || adding) return;
    setAdding(true);
    try {
      for (const card of cards) {
        await setStage.mutateAsync({
          searchId: context,
          candidateId: card.candidateId,
          stage: 'sourced',
        });
        setAdded((was) => new Set([...was, card.candidateId]));
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <div className="drawer-scrim" onClick={close} aria-hidden="true" />
      <div
        className={`drawer drawer--vera ${listOnPhone ? 'drawer--list' : 'drawer--chat'}`}
        role="dialog"
        aria-label="Ask kadmon"
      >
        <aside className="chats">
          <div className="chats__head">
            <Button
              kind="accent"
              size="sm"
              icon="plus"
              onClick={() => {
                setListOnPhone(false);
                talk.hush();
                // A voice conversation's new chat is held, not in the URL, so leaving it for a new
                // one remounts nothing: what it held goes here.
                if (held !== null) {
                  setHeld(null);
                  chatNow.current = null;
                  setFailure(null);
                  setAdded(new Set());
                }
                show(null);
              }}
            >
              New chat
            </Button>
            {/* On a phone this column IS the screen, so it carries its own way out; on a
                desktop the conversation's × is on screen beside it and this one is hidden. */}
            <span className="chats__close">
              <Button kind="quiet" size="sm" icon="x" onClick={close} aria-label="Close" />
            </span>
          </div>
          <div className="chats__list">
            {(chats.data ?? []).map((row) => (
              <ChatRow
                key={row.id}
                chat={row}
                current={row.id === chatId}
                onPick={() => {
                  setListOnPhone(false);
                  if (row.id !== chatId) talk.hush();
                  show(row.id);
                }}
              />
            ))}
            {chats.data?.length === 0 && (
              <p className="micro drawer__note">
                No chats yet. Ask kadmon something and this is where it will be. Every recruiter
                sees every chat, and can carry one on.
              </p>
            )}
          </div>
        </aside>

        <div className="chat">
          <div className="drawer__head">
            <span className="chat__back">
              <Button
                kind="quiet"
                size="sm"
                icon="menu"
                onClick={() => setListOnPhone(true)}
                aria-label="Chats"
              >
                Chats
              </Button>
            </span>
            <VeraOrb
              size={12}
              active={
                asking ||
                talk.mode === 'speaking' ||
                voice.state === 'speaking' ||
                voice.state === 'thinking'
              }
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="drawer__title">{chat.data?.title ?? 'Ask kadmon'}</div>
              {context ? (
                <SearchLine
                  searchId={context}
                  prefill={opener === FIND_FROM_BRIEF && chatId === null && turns.length === 0}
                  onPrefill={setDraft}
                  startedBy={chatId ? (chat.data?.startedBy ?? null) : null}
                />
              ) : (
                <div className="row-meta">
                  {chat.data?.startedBy
                    ? `started by ${chat.data.startedBy}`
                    : 'asks kadmon about people in our database'}
                </div>
              )}
              {chat.data?.private && (
                <div className="micro row-meta">
                  Only you see this chat. <FirmName /> read a private 1-on-1 in it.
                </div>
              )}
            </div>
            <Button kind="quiet" size="sm" icon="x" onClick={close} aria-label="Close" />
          </div>

          <div className="chat__main" data-docked={docked ? '' : undefined}>
            <div className="drawer__body" ref={body}>
              {!enabled ? (
                <p className="micro drawer__note">{NOT_SET_UP}</p>
              ) : turns.length === 0 && pending === null ? (
                <p className="micro drawer__note">
                  Describe who you are looking for: a role, a language, a city, something they told
                  kadmon. kadmon answers with people from our database and the facts behind each; a
                  follow-up narrows the answer. Ask for the top people or a score and she gives
                  them, with the reasons. Or ask her to do something the brain does (enter a
                  candidate, put someone on a search, note, invite, message) and she puts it on a
                  card for you to confirm. Nothing happens until you tap.
                </p>
              ) : (
                // T81: while she is writing, the question she is writing about is drawn once —
                // by the pending block below — and not a second time by the poll, which returns
                // the open log row with no answer on it (`turnsToDraw`).
                turnsToDraw(turns, asking, askedOver).map((turn, at, drawn) => (
                  <TurnView
                    key={turn.queryId}
                    turn={turn}
                    searchId={context}
                    added={added}
                    adding={adding}
                    onAdd={add}
                    imports={imports?.queryId === turn.queryId ? imports : null}
                    selected={selected}
                    {...(voice.state === 'off'
                      ? {
                          onReply: (reply: ReplyTo) => {
                            setReplying(reply);
                            document.getSelection()?.removeAllRanges();
                            composer.current?.focus();
                          },
                        }
                      : {})}
                    // T240: only the newest answer's yes or no is still hers to read.
                    {...(at === drawn.length - 1 && !asking && queue.length === 0
                      ? { onAnswer: (word: string) => void ask(word, false, undefined, true) }
                      : {})}
                  />
                ))
              )}
              {/* T446d: a failed answer's line stays while the queue goes on behind it. */}
              {failure !== null && <p className="micro answer__error">{explain(failure)}</p>}
              {pending !== null && (
                <>
                  <div className="ask" style={{ whiteSpace: 'pre-line' }}>
                    {pendingReply !== null && <Quote text={pendingReply} />}
                    {pending}
                    {pendingFile && (
                      <div className="micro ask__file">
                        <Icon name="clip" size={11} /> {pendingFile}
                      </div>
                    )}
                    <div className="micro ask__who">You</div>
                  </div>
                  <div className="answer">
                    <div className="answer__text">
                      <VeraOrb size={9} />
                      {/* T81: her answer as she writes it. Before the first sentence — and again
                          while she is off reading a screen — the line says which of the two it is.
                          The caret says the words on screen are not the whole answer yet. */}
                      {writing.text === '' ? (
                        <span className="dim">
                          {folding ? 'Reading your new message too…' : waitingLine(writing)}
                        </span>
                      ) : (
                        <div className="answer__md">
                          <Markdown text={writing.text} keepLines caret={!writing.whole} />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
              {queue.map((queued, at) => (
                <div className="ask" key={at} style={{ whiteSpace: 'pre-line' }}>
                  {queued.replyTo && <Quote text={queued.replyTo.shown} />}
                  {queued.words}
                  {queued.file && (
                    <div className="micro ask__file">
                      <Icon name="clip" size={11} /> {queued.file.name}
                    </div>
                  )}
                  <div className="micro ask__who">You</div>
                </div>
              ))}
              {turns.length > 0 && (
                <p className="micro drawer__note">
                  Reasons cite what is on the record and what they told <FirmName />; a score is her
                  read, given when you ask. Nothing is added until you click, and nothing{' '}
                  <FirmName /> proposes is done until you confirm the card. Every question is
                  logged, and every recruiter can read this chat.
                </p>
              )}
            </div>
            <VoiceStage voice={voice} layout={stage.layout} onSwap={stage.swap} />
          </div>

          <div className="drawer__foot">
            {replying && (
              <p className="micro drawer__replying" role="status">
                <span className="drawer__quoted">
                  Replying to {replying.side === 'answer' ? <FirmName /> : 'the question'}
                  {replying.excerpt ? ', the words you highlighted' : ''}: “{replying.shown}”
                </span>
                <button
                  type="button"
                  className="drawer__unattach"
                  onClick={() => setReplying(null)}
                >
                  Take it off
                </button>
              </p>
            )}
            <div className="drawer__compose">
              <textarea
                ref={composer}
                className="input"
                rows={2}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={onComposerKey}
                disabled={!enabled || voice.state !== 'off'}
                readOnly={talk.mode === 'listening'}
                placeholder={
                  voice.state !== 'off'
                    ? 'Voice mode is on. End it to type'
                    : talk.mode === 'listening'
                      ? 'Listening…'
                      : turns.length === 0
                        ? 'Who are you looking for, or what should she do?'
                        : 'Refine, or ask for the next step…'
                }
                aria-label="Ask kadmon"
              />
              <div className="drawer__tools">
                {voice.state === 'off' && (
                  <AttachButton
                    inputRef={attachInput}
                    attached={attached}
                    disabled={!enabled}
                    onChoose={(event) => {
                      const chosen = event.target.files?.[0] ?? null;
                      setTooBig(chosen !== null && chosen.size > attachMaxBytes(chosen));
                      setAttached(chosen);
                    }}
                  />
                )}
                {voice.state === 'off' && <MicButton talk={talk} disabled={!enabled} />}
                <TalkButton
                  voice={voice}
                  disabled={!enabled || asking || queue.length > 0 || talk.mode !== 'idle'}
                />
                <Button
                  kind="accent"
                  icon="sparkle"
                  disabled={
                    !enabled ||
                    (draft.trim() === '' && (attached === null || tooBig)) ||
                    talk.mode === 'listening' ||
                    voice.state !== 'off'
                  }
                  onClick={enqueue}
                >
                  Ask
                </Button>
              </div>
            </div>
            {attached && (
              <p className="micro drawer__attached" role="status">
                <Icon name="clip" size={12} /> {attached.name} · {fileSize(attached.size)}
                {tooBig
                  ? `, too big to read; the most is ${fileSize(attachMaxBytes(attached))}`
                  : ', goes with your next question'}
                <button
                  type="button"
                  className="drawer__unattach"
                  onClick={() => {
                    setAttached(null);
                    setTooBig(false);
                    if (attachInput.current) attachInput.current.value = '';
                  }}
                >
                  Take it off
                </button>
              </p>
            )}
            {talk.note && (
              <p
                className={`micro drawer__voice${talk.note.error ? ' answer__error' : ''}`}
                role="status"
              >
                {talk.note.text}
              </p>
            )}
            {voice.state === 'off' && voice.note && (
              // Why voice mode ended on its own (T66a): nothing heard for a while, or the mic.
              <p className="micro drawer__voice answer__error" role="status">
                {voice.note}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/** One chat in the left column: what was asked first, who started it, when it last moved. */
function ChatRow({
  chat,
  current,
  onPick,
}: {
  chat: ChatSummary;
  current: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      className={`chats__row${current ? ' chats__row--on' : ''}`}
      onClick={onPick}
    >
      <span className="chats__title">{chat.title}</span>
      <span className="micro row-meta">
        {chat.startedBy ?? 'Somebody'} · {formatRelative(chat.lastActivityAt)}
        {chat.searchTitle ? ` · ${chat.searchTitle}` : ''}
        {/* T80a: kadmon read a 1-on-1 of theirs here, so no colleague is shown this chat. */}
        {chat.private ? ' · only you see this' : ''}
      </span>
    </button>
  );
}

/**
 * The line under the title: the mandate the chat carries, who started it when somebody else did,
 * and — when the button on the search page opened it — the role brief into the composer, once.
 */
function SearchLine({
  searchId,
  prefill,
  onPrefill,
  startedBy,
}: {
  searchId: string;
  prefill: boolean;
  onPrefill: (text: string) => void;
  startedBy: string | null;
}) {
  const search = useSearch(searchId);
  const done = useRef(false);
  useEffect(() => {
    if (!prefill || done.current || !search.data) return;
    done.current = true;
    const parts = [search.data.title, search.data.roleBrief, search.data.location]
      .map((part) => part?.trim())
      .filter(Boolean);
    onPrefill(parts.join('. ').replaceAll('..', '.'));
  }, [prefill, search.data, onPrefill]);
  // The mandate, and — on somebody else's chat — who started it. The line stays short on a
  // phone, where it sits under a title that is itself a whole question.
  const parts = [search.data?.title ?? '…', startedBy ? `started by ${startedBy}` : null].filter(
    Boolean,
  );
  return <div className="row-meta">{parts.join(' · ')}</div>;
}

/** T561: names a message of the chat for its Reply: the row and which side of it. */
const replyKey = (queryId: string, side: 'ask' | 'answer') => `${queryId}:${side}`;

/** T561: the message a question replied to, above its words, cut to two lines. */
function Quote({ text }: { text: string }) {
  return <div className="ask__quote">“{text}”</div>;
}

function TurnView({
  turn,
  searchId,
  added,
  adding,
  onAdd,
  imports,
  onAnswer,
  selected,
  onReply,
}: {
  turn: ChatTurn;
  searchId: string | null;
  added: ReadonlySet<string>;
  adding: boolean;
  onAdd: (cards: FindCard[]) => Promise<void>;
  /** T83: the previews this turn's question made of the file it carried, if it carried one. */
  imports: { proposals: ImportProposal[]; file: AskAttachment | null } | null;
  /** T240: asks the next question, "Yes" or "No"; given to the newest answer alone. */
  onAnswer?: (word: string) => void;
  /** T561: the message of the chat holding a highlight, by `replyKey`. */
  selected: { key: string; text: string } | null;
  /** T561: puts a Reply on the composer; absent in voice mode. */
  onReply?: (reply: ReplyTo) => void;
}) {
  // T561: Reply quotes the message, or the words highlighted in it when it holds the selection.
  const replyButton = (side: 'ask' | 'answer', whole: string) => {
    if (!onReply) return null;
    const words = selected?.key === replyKey(turn.queryId, side) ? selected.text : null;
    return (
      <button
        type="button"
        className="turn__reply"
        // Pressing the button would otherwise drop the highlight before the click reads it.
        onMouseDown={(event) => event.preventDefault()}
        onClick={() =>
          onReply({
            queryId: turn.queryId,
            side,
            ...(words ? { excerpt: words } : {}),
            shown: words ?? whole,
          })
        }
      >
        {words ? 'Reply to the highlighted words' : 'Reply'}
      </button>
    );
  };
  const addable = turn.matches.filter(
    (card) => card.stage === null && !added.has(card.candidateId),
  );
  // T240: Yes and No under the newest answer that asks one of its own (answer-yes-no.ts).
  const yesNo = onAnswer !== undefined && yesNoUnder(turn, imports?.proposals.length ?? 0);
  // T429: an unclear ask's readings, a button each; a tap asks its label.
  const options = onAnswer !== undefined ? optionsUnder(turn, imports?.proposals.length ?? 0) : [];
  return (
    <>
      {turn.fromBrain ? (
        // T154f: the brain following up what it was asked earlier — a lookup found, a research run
        // done — written into the chat by itself, not asked by anybody.
        <p className="micro ask__brain">
          <FirmName />, following up for {turn.askedBy ?? 'you'}
        </p>
      ) : (
        <div className="ask">
          {/* T561: the message it replied to, a Reply here or a WhatsApp swipe. */}
          {turn.replyingTo && <Quote text={turn.replyingTo.text} />}
          <span data-reply={replyKey(turn.queryId, 'ask')}>{turn.ask}</span>
          {/* T154a: what came with the question, and what kadmon read of it. */}
          {turn.files && turn.files.length > 0 && <FilesUnder files={turn.files} />}
          <div className="micro ask__who">
            {turn.ask.trim() !== '' && replyButton('ask', turn.ask)}
            {turn.askedBy ?? 'Somebody'}
          </div>
        </div>
      )}
      <div className="answer">
        {turn.answer === null ? (
          // The row is in the log with no answer: the model's reply could not be used, or the
          // question is still in flight in somebody else's tab. Say so; invent nothing.
          <div className="answer__text">
            <VeraOrb size={9} active={false} />
            <span className="answer__error">
              kadmon’s answer could not be used this time. Ask again.
            </span>
          </div>
        ) : (
          <>
            <div className="answer__text">
              <VeraOrb size={9} active={false} />
              <div className="answer__md" data-reply={replyKey(turn.queryId, 'answer')}>
                <Markdown text={turn.answer} keepLines />
              </div>
            </div>
            {onReply && <div className="answer__reply">{replyButton('answer', turn.answer)}</div>}
            {/* T154f: a file she was asked for, under her answer to download. */}
            {turn.downloads && turn.downloads.length > 0 && (
              <ChatDownloads files={turn.downloads} />
            )}
            {turn.matches.map((card) => (
              <MatchCard
                key={card.candidateId}
                card={card}
                searchId={searchId}
                added={added.has(card.candidateId)}
                adding={adding}
                onAdd={() => void onAdd([card])}
              />
            ))}
            {searchId && addable.length > 1 && (
              <div>
                <Button
                  kind="ghost"
                  size="sm"
                  icon="plus"
                  disabled={adding}
                  onClick={() => void onAdd(addable)}
                >
                  Add all {addable.length} to search
                </Button>
              </div>
            )}
            {/* T76: many writes of a kind are one card, with one Confirm (card-groups.ts). */}
            {cardUnits(turn.actions).map((unit) =>
              unit.kind === 'card' ? (
                <Fragment key={unit.card.id}>
                  <ActionCardView card={unit.card} />
                  <DoneSaid lines={doneLinesOf([unit.card])} />
                </Fragment>
              ) : (
                <Fragment key={unit.key}>
                  <GroupCardView unit={unit} />
                  <DoneSaid lines={doneLinesOf(unit.cards)} />
                </Fragment>
              ),
            )}
            {/* T83: the preview of the file this question carried, and its one Import. */}
            {imports?.proposals.map((proposal, at) => (
              <ImportCard key={`${proposal.kind}:${at}`} proposal={proposal} file={imports.file} />
            ))}
            {options.length > 0 &&
              (turn.pickSeveral === true ? (
                <PickSeveral options={options} onSend={onAnswer!} />
              ) : (
                <div className="answer__yesno">
                  {options.map((label) => (
                    <Button
                      key={label}
                      kind="ghost"
                      size="sm"
                      title={label}
                      onClick={() => onAnswer!(label)}
                    >
                      <span className="answer__label">{label}</span>
                    </Button>
                  ))}
                </div>
              ))}
            {yesNo && (
              <div className="answer__yesno">
                <Button kind="ghost" size="sm" onClick={() => onAnswer!('Yes')}>
                  Yes
                </Button>
                <Button kind="ghost" size="sm" onClick={() => onAnswer!('No')}>
                  No
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/**
 * T453 (24 Oct 2026): readings that can go together. Each is a tick; Send, once one is ticked, asks
 * the ticked ones as one line (`@vera/db/picked-options` `joinPicked`). A single pick stays a tap.
 */
function PickSeveral({
  options,
  onSend,
}: {
  options: readonly string[];
  onSend: (word: string) => void;
}) {
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const toggle = (label: string) =>
    setTicked((now) => {
      const next = new Set(now);
      if (!next.delete(label)) next.add(label);
      return next;
    });
  return (
    <div className="answer__yesno answer__pick">
      {options.map((label) => (
        <Button
          key={label}
          kind="ghost"
          size="sm"
          pressed={ticked.has(label)}
          icon={ticked.has(label) ? 'check' : undefined}
          title={label}
          onClick={() => toggle(label)}
        >
          <span className="answer__label">{label}</span>
        </Button>
      ))}
      {ticked.size > 0 && (
        <Button kind="primary" size="sm" onClick={() => onSend(joinPicked(options, ticked))}>
          Send
        </Button>
      )}
    </div>
  );
}

/**
 * T154a (28 Sep 2026): what came with a question, under its words — a file's name or a link's
 * address, whether kadmon could read it, and what it read, folded away until opened. A spreadsheet
 * shows its name alone: its rows went to the import's card, not into a reading.
 */
function FilesUnder({ files }: { files: ChatFile[] }) {
  return (
    <div className="ask__files">
      {files.map((file, at) => (
        <details key={`${file.name}:${at}`} className="ask__file">
          <summary className="micro">
            <Icon name={file.kind === 'link' ? 'link' : 'clip'} size={11} /> {file.name}
            {file.kind === 'sheet' || file.kind === 'reaction'
              ? ''
              : file.status === 'read'
                ? ' · read'
                : ' · not read'}
          </summary>
          {file.reading && <p className="micro ask__reading">{file.reading}</p>}
        </details>
      ))}
    </div>
  );
}

/**
 * T83: the paper-clip. A hidden file input behind a button, so the control looks like the mic and
 * Ask beside it rather than like a browser's own file box, and the chip under the composer says
 * what is attached and takes it off again. One file at a time: an import reads one list, and since
 * T154a a CV, a document or a photo is read with the question.
 */
function AttachButton({
  inputRef,
  attached,
  disabled,
  onChoose,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  attached: File | null;
  disabled: boolean;
  onChoose: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  const label = attached
    ? `Attached: ${attached.name}`
    : 'Attach a file: a CV, a document, a photo, or a list to import';
  return (
    <span className="attach">
      <input
        ref={inputRef}
        type="file"
        accept={ATTACH_ACCEPT}
        className="attach__input"
        onChange={onChoose}
        tabIndex={-1}
        aria-hidden="true"
      />
      <Button
        kind={attached ? 'accent' : 'ghost'}
        icon="clip"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        aria-label={label}
        title={label}
      />
    </span>
  );
}

/**
 * T413: kadmon says what it did once a yes has gone through, in the chat under the card, as on
 * WhatsApp ("Done, Daniel Eisenberg’s email is now daniel@gmail.com."). Words from the server.
 */
function DoneSaid({ lines }: { lines: readonly string[] }) {
  if (lines.length === 0) return null;
  return (
    <div className="answer proposal__said">
      {lines.map((line, i) => (
        <div key={i} className="answer__text">
          <VeraOrb size={9} active={false} />
          <span>{line}</span>
        </div>
      ))}
    </div>
  );
}

/** Where a card stands, in the words a recruiter reads on it. */
const CARD_STATUS: Record<ActionStatus, { label: string; tone: Tone }> = {
  proposed: { label: 'Waiting for you', tone: 'accent' },
  applying: { label: 'Applying…', tone: 'info' },
  waiting: { label: 'Waiting for the record', tone: 'warn' },
  applied: { label: 'Done', tone: 'ok' },
  failed: { label: 'Stopped', tone: 'bad' },
  dismissed: { label: 'Dismissed', tone: 'ghost' },
  // T239: closed by nobody, when a newer card went up in the same WhatsApp chat.
  replaced: { label: 'Replaced', tone: 'ghost' },
};

/**
 * The settle poll of a card WAITING on the worker, from this tab and only while it waits. True
 * once the poll has given up: the card is still waiting, the poll is not. A hook of its own since
 * T76 — a group's cards each wait on their own record.
 */
function useSettlePoll(card: ActionCard): boolean {
  const settle = useSettleAction();
  const queryClient = useQueryClient();
  const [stale, setStale] = useState(false);
  const settleRef = useRef(settle);
  settleRef.current = settle;
  useEffect(() => {
    if (card.status !== 'waiting') return;
    const started = Date.now();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const after = await settleRef.current.mutateAsync(card.id);
        if (cancelled) return;
        if (after.status !== 'waiting') {
          // The last step ran, or stopped: now the lists it could have moved are refetched, once.
          await Promise.all(
            KEYS.action.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
          );
          return;
        }
      } catch {
        // A settle that fails is retried on the next tick; the card says it is waiting either way.
      }
      if (Date.now() - started > POLL_MS.actionSettleTimeout) {
        setStale(true);
        return;
      }
      timer = setTimeout(() => void tick(), POLL_MS.actionSettle);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [card.id, card.status, queryClient]);
  return stale;
}

/**
 * The confirm card. Every step kadmon proposed, in the brain's words; Confirm and Dismiss while
 * it is up; the outcome on the same card afterwards — a tick per step that ran, the form's own
 * sentence under the step that stopped it, "not run" on the rest.
 *
 * A card WAITING on the worker polls the settle route from this tab, only while it waits: a new
 * person is an entry request the worker applies, so "create her and put her on the Search" cannot
 * finish inside Confirm, and the last step runs once the record exists (the import's own shape).
 */
function ActionCardView({ card }: { card: ActionCard }) {
  // T82: the button is the one caller that says it was tapped — what a removal needs.
  const confirm = useConfirmAction();
  const confirmWithFile = useConfirmCardWithFile();
  const dismiss = useDismissAction();
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<unknown>(null);
  const stale = useSettlePoll(card);
  // T360 E3 3b: a Drive card for a file attached in this chat needs the file with the tap; a
  // reload loses it, and the card then says to attach it again.
  const needed = card.steps.find((step) => step.file)?.file ?? null;
  const askedFile = useContext(AskedFiles)[card.queryId] ?? null;
  const fileLost = needed !== null && askedFile === null;
  const busy = confirm.isPending || confirmWithFile.isPending || dismiss.isPending;

  const act = async (run: () => Promise<unknown>) => {
    setFailure(null);
    try {
      await run();
    } catch (error) {
      setFailure(error);
      // A conflict means somebody else acted on this card: refetch, and draw what became of it.
      await queryClient.invalidateQueries({ queryKey: ['chat'] });
    }
  };

  const done = new Set((card.results ?? []).map((r) => r.step));
  const detailOf = (step: number) => card.results?.find((r) => r.step === step)?.detail;
  const status = busy ? CARD_STATUS.applying : CARD_STATUS[card.status];
  const who = card.decidedBy ?? 'somebody';
  const when = formatRelative(card.decidedAt ?? card.createdAt);
  const foot =
    card.status === 'proposed'
      ? `Asked by ${card.proposedBy ?? 'a recruiter'} · ${formatRelative(card.createdAt)}`
      : card.status === 'dismissed'
        ? `Dismissed by ${who} · ${when}`
        : card.status === 'replaced'
          ? `Replaced by a later card on WhatsApp · ${when}`
          : card.status === 'waiting'
            ? stale
              ? `Confirmed by ${who} · the record is taking longer than usual; the last step runs on its own once it exists`
              : `Confirmed by ${who} · the record is being created; the last step runs once it exists`
            : card.status === 'failed'
              ? `Confirmed by ${who} · stopped at step ${(card.failedStep ?? 0) + 1}`
              : `Confirmed by ${who} · ${when}`;

  return (
    <div className={`proposal proposal--${card.status}`}>
      <div className="proposal__head">
        <span>
          kadmon proposes
          {card.removes ? ' a removal' : card.steps.length > 1 ? ` ${card.steps.length} steps` : ''}
        </span>
        <Badge tone={status.tone} dot={card.status === 'applying' || card.status === 'waiting'}>
          {status.label}
        </Badge>
      </div>
      <ol className="proposal__steps">
        {card.steps.map((step, i) => {
          const isDone = done.has(i);
          const isFailed = card.status === 'failed' && card.failedStep === i;
          const skipped =
            card.status === 'dismissed' ||
            card.status === 'replaced' ||
            (card.status === 'failed' && card.failedStep !== null && i > card.failedStep);
          const classes = [
            'proposal__step',
            isDone && 'proposal__step--done',
            isFailed && 'proposal__step--failed',
            skipped && 'proposal__step--skipped',
          ]
            .filter(Boolean)
            .join(' ');
          const detail = detailOf(i);
          return (
            <li key={i} className={classes}>
              <span className="proposal__mark" aria-hidden="true">
                {isDone ? '✓' : isFailed ? '!' : i + 1}
              </span>
              <span>
                {step.summary}
                {detail && <span className="proposal__detail"> · {detail}</span>}
                {/* T62: once the message step has run, where the message waits. */}
                {isDone && step.contact && (
                  <span className="proposal__detail">
                    {' · '}
                    <Link
                      to={`/crm/clients/${step.contact.clientId}/contacts/${step.contact.contactId}`}
                    >
                      Open the message
                    </Link>
                  </span>
                )}
                {skipped && card.status === 'failed' && (
                  <span className="proposal__detail"> · not run</span>
                )}
                {isFailed && card.failure && (
                  <div className="proposal__failure">{card.failure}</div>
                )}
              </span>
            </li>
          );
        })}
      </ol>
      <div className="proposal__foot">
        <span className="micro row-meta">
          {foot}
          {card.status === 'proposed' &&
            fileLost &&
            needed &&
            ` · ${needed.name} is not here any more: attach it again, then ask again`}
        </span>
        {card.status === 'proposed' && (
          <div className="proposal__buttons">
            <Button
              kind={card.secondYes ? 'danger' : 'accent'}
              size="sm"
              icon="check"
              disabled={busy || fileLost}
              onClick={() =>
                void act(() =>
                  askedFile !== null && needed !== null
                    ? confirmWithFile.mutateAsync({ actionId: card.id, file: askedFile })
                    : confirm.mutateAsync(card.id),
                )
              }
            >
              {card.removes ? 'Confirm the removal' : 'Confirm'}
            </Button>
            <Button
              kind="ghost"
              size="sm"
              icon="x"
              disabled={busy}
              onClick={() => void act(() => dismiss.mutateAsync(card.id))}
            >
              Dismiss
            </Button>
          </div>
        )}
      </div>
      {failure !== null && (
        <p className="micro answer__error" style={{ padding: '0 14px 10px' }}>
          {explainAction(failure)}
        </p>
      )}
    </div>
  );
}

/**
 * T76: many writes of a kind, one card — "message these eight", "note on all three". Each item is
 * a card of its own underneath (its own steps, its own outcome, applied by its own request), so
 * every message's words are here to be read, one Confirm applies them in order, and an item the
 * brain refuses — a window that closed since — stops alone, with the form's sentence under it,
 * while the rest go. An item can be taken off before the yes; nothing here applies anything the
 * recruiter has not had in front of them.
 */
function GroupCardView({ unit }: { unit: Extract<CardUnit, { kind: 'group' }> }) {
  const confirmAll = useConfirmGroup();
  const dismissAll = useDismissGroup();
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<unknown>(null);
  const busy = confirmAll.isPending || dismissAll.isPending;
  const tally = tallyOf(unit.cards);
  const up = unit.cards.filter((card) => card.status === 'proposed').map((card) => card.id);

  const act = async (run: () => Promise<unknown>) => {
    setFailure(null);
    try {
      await run();
    } catch (error) {
      // The run stopped where it was (the network): what it reached shows on each item.
      setFailure(error);
      await queryClient.invalidateQueries({ queryKey: ['chat'] });
    }
  };

  const reached = tally.total - tally.proposed - tally.applying;
  const status: { label: string; tone: Tone } =
    busy || tally.applying > 0
      ? { label: `Applying… ${reached} of ${tally.total}`, tone: 'info' }
      : tally.proposed > 0
        ? CARD_STATUS.proposed
        : tally.waiting > 0
          ? CARD_STATUS.waiting
          : tally.failed > 0
            ? { label: `${tally.failed} stopped`, tone: 'bad' }
            : tally.applied > 0
              ? CARD_STATUS.applied
              : tally.replaced > 0
                ? CARD_STATUS.replaced
                : CARD_STATUS.dismissed;
  const state =
    tally.proposed > 0
      ? 'proposed'
      : tally.failed > 0
        ? 'failed'
        : tally.applied + tally.waiting > 0
          ? 'applied'
          : 'dismissed';
  const first = unit.cards[0]!;
  const decided = unit.cards.find((card) => card.decidedBy !== null && card.status !== 'dismissed');
  const counts = [
    tally.applied > 0 && `${tally.applied} done`,
    tally.waiting > 0 && `${tally.waiting} waiting for the record`,
    tally.failed > 0 && `${tally.failed} stopped`,
    tally.dismissed > 0 && `${tally.dismissed} taken off`,
    tally.replaced > 0 && `${tally.replaced} replaced by a later card`,
  ]
    .filter(Boolean)
    .join(' · ');
  const foot =
    tally.proposed === tally.total
      ? `Asked by ${first.proposedBy ?? 'a recruiter'} · ${formatRelative(first.createdAt)}`
      : decided
        ? `Confirmed by ${decided.decidedBy ?? 'somebody'} · ${counts}`
        : counts;

  return (
    <div className={`proposal proposal--group proposal--${state}`}>
      <div className="proposal__head">
        <span>kadmon proposes {groupNoun(unit.tool, tally.total)}</span>
        <Badge tone={status.tone} dot={busy || tally.applying > 0 || tally.waiting > 0}>
          {status.label}
        </Badge>
      </div>
      <ol className="proposal__items">
        {unit.cards.map((card, i) => (
          <GroupItem key={card.id} card={card} index={i} busy={busy} />
        ))}
      </ol>
      <div className="proposal__foot">
        <span className="micro row-meta">{foot}</span>
        {up.length > 0 && (
          <div className="proposal__buttons">
            <Button
              kind="accent"
              size="sm"
              icon="check"
              disabled={busy}
              onClick={() => void act(() => confirmAll.mutateAsync(up))}
            >
              {`Confirm ${up.length === tally.total ? 'all ' : ''}${up.length}`}
            </Button>
            <Button
              kind="ghost"
              size="sm"
              icon="x"
              disabled={busy}
              onClick={() => void act(() => dismissAll.mutateAsync(up))}
            >
              Dismiss
            </Button>
          </div>
        )}
      </div>
      {failure !== null && (
        <p className="micro answer__error" style={{ padding: '0 14px 10px' }}>
          {explainAction(failure)}
        </p>
      )}
    </div>
  );
}

/** One item of a group: a card's steps as lines, its own outcome, and a way off the card. */
function GroupItem({ card, index, busy }: { card: ActionCard; index: number; busy: boolean }) {
  const dismiss = useDismissAction();
  const stale = useSettlePoll(card);
  const done = new Set((card.results ?? []).map((r) => r.step));
  const mark =
    card.status === 'applied'
      ? '✓'
      : card.status === 'failed'
        ? '!'
        : card.status === 'dismissed' || card.status === 'replaced'
          ? '-'
          : index + 1;
  return (
    <li className={`proposal__item proposal__item--${card.status}`}>
      <span className="proposal__mark" aria-hidden="true">
        {mark}
      </span>
      <div className="proposal__lines">
        {card.steps.map((step, i) => {
          const isFailed = card.status === 'failed' && card.failedStep === i;
          const notRun =
            card.status === 'failed' && card.failedStep !== null && i > card.failedStep;
          const detail = card.results?.find((r) => r.step === i)?.detail;
          return (
            <div
              key={i}
              className={[
                'proposal__line',
                notRun && 'proposal__line--skipped',
                // What comes before the write itself (the pause before a message) reads quieter.
                i < card.steps.length - 1 && 'proposal__line--pre',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {step.summary}
              {detail &&
                (step.tool === 'connect_google' && detail.startsWith('https://') ? (
                  // T570a: the link to Google's own screen, which only its owner passes through.
                  <span className="proposal__detail">
                    {' · '}
                    <a href={detail} target="_blank" rel="noreferrer">
                      Open Google
                    </a>
                  </span>
                ) : (
                  <span className="proposal__detail"> · {detail}</span>
                ))}
              {done.has(i) && step.contact && (
                <span className="proposal__detail">
                  {' · '}
                  <Link
                    to={`/crm/clients/${step.contact.clientId}/contacts/${step.contact.contactId}`}
                  >
                    Open the message
                  </Link>
                </span>
              )}
              {notRun && <span className="proposal__detail"> · not run</span>}
              {isFailed && card.failure && <div className="proposal__failure">{card.failure}</div>}
            </div>
          );
        })}
        {card.status === 'waiting' && (
          <div className="proposal__detail">
            {stale
              ? 'The record is taking longer than usual; the last step runs on its own once it exists'
              : 'Waiting for the record; the last step runs once it exists'}
          </div>
        )}
        {card.status === 'dismissed' && <div className="proposal__detail">Taken off the card</div>}
      </div>
      {card.status === 'proposed' && (
        <button
          type="button"
          className="proposal__remove"
          aria-label="Take this one off the card"
          title="Take this one off the card"
          disabled={busy || dismiss.isPending}
          onClick={() => void dismiss.mutateAsync(card.id).catch(() => {})}
        >
          ×
        </button>
      )}
    </li>
  );
}

/** Why a tap did not take, in a sentence a recruiter can act on. */
function explainAction(error: unknown): string {
  const code = error instanceof ApiError ? error.code : null;
  switch (code) {
    case 'conflict':
      return 'Somebody already acted on this card. It shows what became of it.';
    case 'not_found':
      return 'This card is not here any more.';
    // T82: only reachable by something other than the button — said plainly all the same.
    case 'file_needed':
      return 'The file is not here any more: attach it again in the chat and ask again.';
    case 'second_yes_required':
      return 'One yes is not enough for this card: confirm it with the button here.';
    case 'network':
      return 'The brain API did not answer. Is it running?';
    default:
      return `Could not apply this${code ? ` (${code})` : ''}. The card shows how far it got.`;
  }
}

function MatchCard({
  card,
  searchId,
  added,
  adding,
  onAdd,
}: {
  card: FindCard;
  searchId: string | null;
  added: boolean;
  adding: boolean;
  onAdd: () => void;
}) {
  const sub =
    [card.currentTitle, card.currentEmployer, card.location].filter(Boolean).join(' · ') || '-';
  return (
    <div className="match">
      <div style={{ minWidth: 0 }}>
        <div className="match__who">
          <Avatar name={card.fullName} size={28} />
          <div style={{ minWidth: 0 }}>
            <Link to={`/candidates/${card.candidateId}`} className="match__name row-link">
              {card.fullName ?? 'Unnamed'}
            </Link>
            <div className="row-meta">{sub}</div>
          </div>
        </div>
        <ul className="match__reason">
          {card.reasons.map((reason, i) => (
            <li key={i}>
              · {reason.why} - <span className="match__quote">“{reason.quote}”</span>{' '}
              <span className="match__source">({SOURCE_LABEL[reason.source]})</span>
            </li>
          ))}
        </ul>
      </div>
      {card.stage ? (
        <Badge tone="ghost">{STAGE_LABELS[card.stage]}</Badge>
      ) : added ? (
        <Badge tone="ok" dot>
          Sourced
        </Badge>
      ) : searchId ? (
        <Button kind="accent" size="sm" icon="plus" disabled={adding} onClick={onAdd}>
          Add to search
        </Button>
      ) : null}
    </div>
  );
}

/** Why a question got no answer, in a sentence a recruiter can act on. */
function explain(error: unknown): string {
  const code = error instanceof ApiError ? error.code : null;
  switch (code) {
    // The number is `FIND_RATE_LIMIT` (services/bff/src/find.ts): thirty until T75 raised it to
    // ninety on 19 Sep 2026, which this sentence had not caught up with.
    case 'rate_limited':
      return 'kadmon has done ninety calls for you in ten minutes. An ask that acts costs two or three. Give it a few minutes, then ask again.';
    case 'find_failed':
      return 'kadmon’s answer could not be used this time. Ask again.';
    // 17 Sep 2026: voice mode spoke the answer and the chat keeps it; its people did not come.
    case 'find_incomplete':
      return 'kadmon answered, but the people behind her answer could not be listed this time. Ask again to see them.';
    case 'find_unavailable':
      return NOT_SET_UP;
    case 'not_found':
      return 'That chat or search is not in this tenant any more.';
    case 'search_not_live':
      return 'That search is not live any more, so kadmon will not answer on it. Start a new chat.';
    // T561: the BFF refused the Reply before asking; the words are back on the composer.
    case 'reply_not_found':
      return 'kadmon could not find that message in this chat, so she was not asked. Your question is back in the box: press Reply on the message again, then send it.';
    case 'network':
      return 'The brain API did not answer. Is it running?';
    default:
      return `Could not ask kadmon${code ? ` (${code})` : ''}.`;
  }
}

/**
 * The search page's door. A sentence rather than a disabled button when the server cannot find:
 * a dead button is worse than no button (design doc § 3), and the sentence says where the key goes.
 */
export function FindButton() {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  if (me.data && !me.data.findEnabled) {
    return (
      <span className="micro" title={NOT_SET_UP}>
        Find candidates · not set up on this server
      </span>
    );
  }
  return (
    <Button
      kind="accent"
      icon="sparkle"
      onClick={() =>
        setParams(withParams(params, { [FIND_PARAM]: FIND_FROM_BRIEF, [CHAT_PARAM]: undefined }), {
          replace: true,
        })
      }
    >
      Find candidates
    </Button>
  );
}
